import React from 'react';
import axiosInstance, { endpoints } from '@/lib/axios';
import type { ILeadPayload, LeadOrigin } from '@/types/lead';

interface ContactModalProps {
    isOpen: boolean;
    onClose: () => void;
    origin?: LeadOrigin;
    message?: string;
    /** Formation-specific callers (see Fiche/FicheStickyCta) — without these the
     *  lead lands with nothing in the admin's "Formation" / "Thème" columns. */
    formationTitle?: string;
    formationTheme?: string;
}

const ContactModal: React.FC<ContactModalProps> = ({
    isOpen,
    onClose,
    origin = 'contact',
    message = "Demande d'orientation formation depuis la page d'accueil.",
    formationTitle,
    formationTheme,
}) => {
    const [formData, setFormData] = React.useState({
        prenom: '',
        nom: '',
        email: '',
        phone: '',
    });
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState('');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const isFormValid = Object.values(formData).every(value => value.trim() !== '');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isFormValid) return;

        setSubmitting(true);
        setError('');
        try {
            const payload: ILeadPayload = {
                first_name: formData.prenom,
                last_name: formData.nom,
                email: formData.email,
                phone: `+33${formData.phone}`,
                message,
                formation_title: formationTitle,
                // Capped: the API validates formation_theme at 150 characters.
                formation_theme: formationTheme?.slice(0, 150),
                origin,
            };

            await axiosInstance.post(endpoints.leads.create, payload);
            setFormData({ prenom: '', nom: '', email: '', phone: '' });
            onClose();
        } catch {
            setError("Une erreur est survenue lors de l'envoi. Veuillez réessayer.");
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#FFFFFF4D] backdrop-blur-xs">
            <div className="relative w-full max-w-170 bg-white rounded-lg pt-14 pb-6 sm:pb-12 px-5 sm:px-10 border border-[#40BFDE]" style={{ boxShadow: "0px 1px 2px 1px #00000014" }}>
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-5 right-5 text-[#1B365E] hover:text-[#40BFDE] transition-colors cursor-pointer duration-75"
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_40006002_6942)">
                            <path d="M23.2897 4.0987C24.2268 3.16164 24.2268 1.63986 23.2897 0.702796C22.3526 -0.234265 20.8309 -0.234265 19.8938 0.702796L12 8.60409L4.0987 0.710292C3.16164 -0.226769 1.63986 -0.226769 0.702796 0.710292C-0.234265 1.64735 -0.234265 3.16914 0.702796 4.1062L8.60409 12L0.710292 19.9013C-0.226769 20.8384 -0.226769 22.3601 0.710292 23.2972C1.64735 24.2343 3.16914 24.2343 4.1062 23.2972L12 15.3959L19.9013 23.2897C20.8384 24.2268 22.3601 24.2268 23.2972 23.2897C24.2343 22.3526 24.2343 20.8309 23.2972 19.8938L15.3959 12L23.2897 4.0987Z" fill="currentColor" />
                        </g>
                        <defs>
                            <clipPath id="clip0_40006002_6942">
                                <rect width="24" height="24" fill="white" />
                            </clipPath>
                        </defs>
                    </svg>
                </button>

                {/* Header */}
                <div className="text-center mb-6">
                    <h2 className="font-red-hat font-light text-[22px] sm:text-[30px] leading-7 sm:leading-10 text-[#1B365E] mb-4.5 tracking-[0.9px]">
                        Vous ne savez pas encore quelle formation <br className="hidden md:block" />
                        est la meilleure option pour vous ?
                    </h2>
                    <p className="font-medium text-[16px] sm:text-[20px] leading-5.5 sm:leading-7.5 text-[#1B365E] tracking-[0.4px]">
                        Nous sommes là pour bien vous orienter
                    </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6 max-w-103 mx-auto">
                    <div>
                        <input
                            type="text"
                            name="prenom"
                            placeholder="Prénom"
                            value={formData.prenom}
                            onChange={handleChange}
                            className="w-full h-10 px-3 rounded-lg border border-[#EFEFEF] bg-white placeholder:text-[#ABABAB] text-[12px] font-normal text-[#1B365E] focus:outline-none focus:border-[#40BFDE] focus:ring-1 focus:ring-[#40BFDE]"
                        />
                    </div>
                    <div>
                        <input
                            type="text"
                            name="nom"
                            placeholder="Nom"
                            value={formData.nom}
                            onChange={handleChange}
                            className="w-full h-10 px-3 rounded-lg border border-[#EFEFEF] bg-white placeholder:text-[#ABABAB] text-[12px] font-normal text-[#1B365E] focus:outline-none focus:border-[#40BFDE] focus:ring-1 focus:ring-[#40BFDE]"
                        />
                    </div>
                    <div>
                        <input
                            type="email"
                            name="email"
                            placeholder="Email"
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full h-10 px-3 rounded-lg border border-[#EFEFEF] bg-white placeholder:text-[#ABABAB] text-[12px] font-normal text-[#1B365E] focus:outline-none focus:border-[#40BFDE] focus:ring-1 focus:ring-[#40BFDE]"
                        />
                    </div>
                    <div className="flex gap-2.5 sm:gap-4.5">
                        <div className="w-14 h-10 px-3 flex items-center justify-center rounded-lg border border-[#EFEFEF] bg-white text-[12px] font-normal text-[#ABABAB]">
                            +33
                        </div>
                        <input
                            type="tel"
                            name="phone"
                            placeholder="Portable"
                            value={formData.phone}
                            onChange={handleChange}
                            className="w-full h-10 px-3 rounded-lg border border-[#EFEFEF] bg-white placeholder:text-[#ABABAB] text-[12px] font-normal text-[#1B365E] focus:outline-none focus:border-[#40BFDE] focus:ring-1 focus:ring-[#40BFDE]"
                        />
                    </div>

                    <div className="text-[12px] text-[#1B365E] font-medium tracking-[0.25px]">
                        *Tous les champs sont obligatoires
                    </div>

                    {error && <p className="text-[12px] text-red-600">{error}</p>}

                    <button
                        type="submit"
                        className={`w-full h-12 sm:h-14.5 border font-bold text-[16px] rounded-lg transition-all duration-300 ${isFormValid && !submitting
                            ? "bg-[#1B365E] border-[#1B365E] text-white hover:bg-[#152b4d] cursor-pointer"
                            : "bg-[#D9D9D9] border-[#ABABAB] text-[#9CA3AF] cursor-not-allowed"
                            }`}
                        disabled={!isFormValid || submitting}
                    >
                        {submitting ? "Envoi..." : "Créer mon dossier de candidature"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ContactModal;
